/**
 * 承保状态的**中文名与色彩**（唯一一份 · 台账列表与表单共用）
 *
 * ## 🔴 为什么抽成单独文件
 *
 * 保单表单与保单列表**都要**把 `PolicyStatus` 显示成中文、都要按状态取色。
 * 两份各写一遍 = 同一事实两个载体（铁律 24）—— 而且它们**会漂移**
 * （改了列表忘了表单，同一个状态在两处显示成不同词）。
 *
 * ## 边界：这是"文案"，不是"口径"
 *
 * | 判定 | 谁定 |
 * |---|---|
 * | **哪些状态计入净资产**（`active` / `paid_up`） | 🔴 **共享层内核** `cashValueCountsInNetWorth()`，经 `PolicyView.cashValueCounted` 下发 —— 端侧**不得**自己写 `status === 'active'` |
 * | 状态取值集合（6 态） | 共享层 `PolicyStatus` / `POLICY_STATUSES` |
 * | 中文名 / Chip 颜色 | 本文件（纯展示，无判定语义） |
 *
 * ⚠️ 中文名与 `POLICY_STATUSES`（内核）**必须一一对应**：`policy-status.spec.ts` 有一条
 * 「无遗漏、无多余」的断言 —— 内核加一态而这里忘了加，测试立刻红（而不是界面露出英文原值）。
 */

import { POLICY_STATUSES, PolicyStatus, type PolicyStatus as PolicyStatusType } from '@jiaying/shared';

/**
 * 状态 → 中文名。
 *
 * 用 `Record<PolicyStatus, string>`（**闭合**）而不是 `Record<string, string>`：
 * 前者漏掉任一取值都是**编译错误**，后者只会静默漏。
 */
export const POLICY_STATUS_LABEL: Record<PolicyStatusType, string> = {
  [PolicyStatus.Active]: '生效中',
  [PolicyStatus.PaidUp]: '已缴清',
  [PolicyStatus.Lapsed]: '已失效',
  [PolicyStatus.Claimed]: '已理赔',
  [PolicyStatus.Surrendered]: '已退保',
  [PolicyStatus.Matured]: '已满期',
};

/** 状态 → MUI Chip 颜色（中性 / 正 / 警示 —— 🔴 不用 `error`：失效不是"错误"，是保单的一种归宿） */
const STATUS_COLOR: Record<PolicyStatusType, 'success' | 'default' | 'warning'> = {
  [PolicyStatus.Active]: 'success',
  [PolicyStatus.PaidUp]: 'success',
  [PolicyStatus.Lapsed]: 'warning',
  [PolicyStatus.Claimed]: 'default',
  [PolicyStatus.Surrendered]: 'default',
  [PolicyStatus.Matured]: 'default',
};

/** 取中文名；未知取值**回退原值**（不猜、不空着 —— 与 `currencySymbolOf` 的未知回退同精神） */
export function policyStatusLabel(status: string): string {
  return POLICY_STATUS_LABEL[status as PolicyStatusType] ?? status;
}

/** 取 Chip 颜色；未知取值按中性 */
export function policyStatusColor(status: string): 'success' | 'default' | 'warning' {
  return STATUS_COLOR[status as PolicyStatusType] ?? 'default';
}

/** 表单下拉用的 `{ value, label }`（顺序 = 内核 `POLICY_STATUSES` 的顺序 = 生命周期） */
export const POLICY_STATUS_OPTIONS: readonly { value: PolicyStatusType; label: string }[] =
  POLICY_STATUSES.map((s) => ({ value: s, label: POLICY_STATUS_LABEL[s] }));
